import { useEffect, useRef, useState } from 'react';
import { Modal } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

import CommonModalButton from '../../../../form-components/CommonModalButton';
import { createContractLogoSeal, updateContractLogoSeal } from '../../../../api/contract';
import { ModalHeader, HeaderTitle, CloseButton, ModalContent, Card, FooterRow } from './LogoSealModalStyled';
import {
  EditContainer,
  FieldGroup,
  FieldLabel,
  TitleInput,
  UploadGroup,
  UploadHintRow,
  UploadHintLabel,
  UploadHintText,
  UploadBox,
  ImagePreview,
  ReuploadButton,
  BottomNote,
} from './LogoSealEditModalStyled';

// 타입별 안내 문구 (로고 4:3 / 직인·서명 1:1)
const HINTS = {
  LOGO: '*5MB 미만의 PNG, JPG, JPEG 이미지 파일 ( 4:3 )',
  SEAL: '*5MB 미만의 PNG, JPG, JPEG 이미지 파일 ( 1:1 )',
};

// 로고/직인 단건 등록·수정 모달.
// - item 이 없으면 등록(POST), 있으면 수정(PUT)
// - 이미지는 multipart 로 파일 자체를 전송(imageFile). 수정 시 새 파일이 없으면 기존 imageUrl 유지.
const LogoSealEditModal = ({ open, branchId, type, item, onClose }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const token = useSelector(state => state.user.user?.token);
  const fileInputRef = useRef(null);

  const isEdit = !!item;
  const typeLabel = type === 'SEAL' ? t('직인') : t('로고');

  const [title, setTitle] = useState('');
  // 새로 선택한 파일 (없으면 null → 기존 이미지 유지)
  const [imageFile, setImageFile] = useState(null);
  // 화면 미리보기 URL (기존 imageUrl 또는 새 파일의 object URL)
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (open) {
      setTitle(item?.title || '');
      setImageFile(null);
      setPreviewUrl(item?.imageUrl || '');
    }
  }, [open, item]);

  const openFilePicker = () => fileInputRef.current?.click();

  const handleFileChange = e => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 같은 파일 재선택 허용
    if (!file) return;
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImageFile(null);
    setPreviewUrl('');
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      if (isEdit) {
        // 새 파일이 있으면 imageFile, 없으면 기존 URL 유지
        return updateContractLogoSeal(
          item.id,
          { title, imageFile, imageUrl: imageFile ? undefined : item.imageUrl },
          token,
        );
      }
      return createContractLogoSeal({ branchId, type, title, imageFile }, token);
    },
    onSuccess: () => {
      toast.success(isEdit ? t('성공적으로 수정되었습니다') : t('성공적으로 생성되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['contractLogoSeal', branchId] });
      onClose();
    },
  });

  const handleSave = () => {
    if (!previewUrl) return toast.error(t('이미지를 등록해 주세요.'));
    saveMutation.mutate();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <EditContainer>
        <ModalHeader>
          <HeaderTitle>{`${typeLabel} ${isEdit ? t('수정') : t('등록')}`}</HeaderTitle>
          <CloseButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <Card>
            <FieldGroup>
              <FieldLabel>{t('제목')}</FieldLabel>
              <TitleInput
                placeholder={t('제목을 입력해 주세요.')}
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
            </FieldGroup>

            <UploadGroup>
              <UploadHintRow>
                <UploadHintLabel>{t('사진 업로드')}</UploadHintLabel>
                <UploadHintText>{t(HINTS[type] || HINTS.SEAL)}</UploadHintText>
              </UploadHintRow>
              {previewUrl ? (
                <ImagePreview>
                  <img src={previewUrl} alt={typeLabel} />
                </ImagePreview>
              ) : (
                <UploadBox onClick={openFilePicker}>
                  <AddIcon sx={{ fontSize: 24, color: '#A6A6A6' }} />
                </UploadBox>
              )}
            </UploadGroup>

            {previewUrl && <ReuploadButton onClick={openFilePicker}>{t('이미지 재업로드')}</ReuploadButton>}
            {previewUrl && <ReuploadButton onClick={removeImage}>{t('이미지 삭제')}</ReuploadButton>}

            <BottomNote>{t('배경이 없는 PNG 이미지 업로드를 권장합니다.')}</BottomNote>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              hidden
              onChange={handleFileChange}
            />
          </Card>

          <FooterRow>
            <CommonModalButton variant="cancel" onClick={onClose}>
              {t('취소')}
            </CommonModalButton>
            <CommonModalButton
              variant="submit"
              onClick={handleSave}
              disabled={saveMutation.isPending}
              style={{ width: 200 }}>
              {t('저장')}
            </CommonModalButton>
          </FooterRow>
        </ModalContent>
      </EditContainer>
    </Modal>
  );
};

export default LogoSealEditModal;
